import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminApi } from '../services/api';
import { planLabel } from '../utils/planLabels';

const RANGES = [7, 30, 90, 180];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PRODUCTS = { firstlook: 'First Look', deepdive: 'Deep Dive', rabbithole: 'Rabbit Hole', course: 'Course' };
const ENTRIES = { open: 'Opened', product: 'Switched to' };
const DEFAULT_AUDIENCES = [
  { id: 'client', label: 'Client' },
  { id: 'tier_0', label: 'Free Tier' },
  { id: 'tier_1', label: 'Tier 1' },
  { id: 'tier_2', label: 'Tier 2' },
  { id: 'tier_3', label: 'Tier 3' },
  { id: 'employee', label: 'Employee' },
  { id: 'admin', label: 'Admin' }
];
const DEFAULT_SELECTED_AUDIENCES = DEFAULT_AUDIENCES
  .filter(({ id }) => id !== 'employee' && id !== 'admin')
  .map(({ id }) => id);

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const minutes = Math.round(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h${minutes % 60 ? ` ${minutes % 60}m` : ''}`;
};

const formatDay = (day, options = { month: 'short', day: 'numeric' }) => {
  const [year, month, date] = String(day).split('-').map(Number);
  return new Date(year, month - 1, date).toLocaleDateString(undefined, options);
};

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const hourLabel = (hour) => `${hour % 12 || 12}${hour < 12 ? 'a' : 'p'}`;
const productLabel = (product) => PRODUCTS[product] || product || 'Report';
const matches = (text, query) => String(text || '').toLowerCase().includes(query);

const Split = ({ item }) => <span className="activity-split">
  <span title="First Look opens"><i className="activity-swatch firstlook" />{item.firstlook}</span>
  <span title="Deep Dive opens"><i className="activity-swatch deepdive" />{item.deepdive}</span>
  {item.other > 0 && <span title="Other report opens"><i className="activity-swatch other" />{item.other}</span>}
</span>;

const Share = ({ value, max }) => <span className="activity-share" aria-hidden="true"><span style={{ width: `${max ? Math.max(4, (value / max) * 100) : 0}%` }} /></span>;

const DailyChart = ({ days }) => {
  const peak = Math.max(0, ...days.map((day) => day.opens));
  const scale = Math.max(1, peak);
  const every = Math.max(1, Math.ceil(days.length / 10));
  const total = days.reduce((sum, day) => sum + day.opens, 0);
  return <figure className="activity-chart">
    <div className="activity-bars" role="img" aria-label={`${total} report opens over ${days.length} days, peaking at ${peak} in a day`}>
      {days.map((day, index) => <div className="activity-bar" key={day.day} title={`${formatDay(day.day, { weekday: 'short', month: 'short', day: 'numeric' })}: ${day.opens} opens (${day.firstlook} First Look, ${day.deepdive} Deep Dive${day.other ? `, ${day.other} other` : ''}) · ${day.readers} readers`}>
        <div className="activity-stack" style={{ height: `${(day.opens / scale) * 100}%` }}>
          {['other', 'deepdive', 'firstlook'].map((bucket) => day[bucket] > 0 && <span key={bucket} className={bucket} style={{ flexGrow: day[bucket] }} />)}
        </div>
        <small>{index % every === 0 || index === days.length - 1 ? formatDay(day.day) : ''}</small>
      </div>)}
    </div>
    <table className="sr-only" aria-label="Daily report opens and product split">
      <thead><tr><th scope="col">Day</th><th scope="col">Opens</th><th scope="col">First Look</th><th scope="col">Deep Dive</th><th scope="col">Other</th><th scope="col">Readers</th></tr></thead>
      <tbody>{days.map((day) => <tr key={day.day}>
        <th scope="row">{formatDay(day.day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</th>
        <td>{day.opens}</td><td>{day.firstlook}</td><td>{day.deepdive}</td><td>{day.other}</td><td>{day.readers}</td>
      </tr>)}</tbody>
    </table>
    <figcaption className="activity-legend">
      <span><i className="activity-swatch firstlook" />First Look</span>
      <span><i className="activity-swatch deepdive" />Deep Dive</span>
      <span><i className="activity-swatch other" />Other</span>
      <span>Peak {peak} / day</span>
    </figcaption>
  </figure>;
};

const HourHeatmap = ({ grid, zone }) => {
  const peak = Math.max(1, ...grid.flat());
  return <figure className="activity-heatmap">
    <table aria-label={`Report opens by weekday and hour (${zone})`}>
      <thead><tr><th aria-hidden="true" />{Array.from({ length: 24 }, (_, hour) => <th key={hour} scope="col">{hour % 3 === 0 ? hourLabel(hour) : <span className="sr-only">{hourLabel(hour)}</span>}</th>)}</tr></thead>
      <tbody>{grid.map((hours, weekday) => <tr key={WEEKDAYS[weekday]}>
        <th scope="row">{WEEKDAYS[weekday]}</th>
        {hours.map((count, hour) => <td key={hour} title={`${WEEKDAYS[weekday]} ${hourLabel(hour)}: ${count} opens`}>
          <span style={count ? { opacity: 0.18 + 0.82 * (count / peak) } : undefined} className={count ? 'hot' : ''}><span className="sr-only">{count}</span></span>
        </td>)}
      </tr>)}</tbody>
    </table>
    <figcaption className="activity-legend"><span>Times shown in {zone}</span><span>Darker = more opens (peak {peak})</span></figcaption>
  </figure>;
};

const UserActivityPanel = ({ query = '', viewTabs = null }) => {
  const zone = useMemo(browserZone, []);
  const [days, setDays] = useState(30);
  const [audiences, setAudiences] = useState(DEFAULT_SELECTED_AUDIENCES);
  const [audienceOptions, setAudienceOptions] = useState(DEFAULT_AUDIENCES);
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [expandedUser, setExpandedUser] = useState('');
  const [expandedCompany, setExpandedCompany] = useState('');
  const requestGeneration = useRef(0);

  useEffect(() => {
    let active = true;
    adminApi.plans().then((result) => {
      const plans = Object.entries(result?.catalog?.plans || {});
      if (!active || !plans.length) return;
      setAudienceOptions([
        ...plans.map(([id, plan]) => ({ id, label: planLabel(id, plan.label) })),
        { id: 'admin', label: 'Admin' }
      ]);
      setAudiences(plans.map(([id]) => id).filter((id) => id !== 'employee'));
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const data = await adminApi.reportViews({ days, tz: zone, plans: audiences });
      if (generation === requestGeneration.current) setState({ loading: false, data, error: '' });
    } catch (err) {
      if (generation === requestGeneration.current) {
        setState((current) => ({ ...current, loading: false, error: err.message }));
      }
    }
  }, [days, audiences, zone]);

  useEffect(() => {
    load();
    return () => { requestGeneration.current += 1; };
  }, [load]);

  const data = state.data;
  const needle = query.trim().toLowerCase();
  const users = useMemo(() => (data?.users || []).filter((user) => !needle || matches(user.email, needle)
    || (user.companies || []).some((company) => matches(company.ticker, needle))), [data, needle]);
  const companies = useMemo(() => (data?.companies || []).filter((company) => !needle || matches(company.ticker, needle)
    || (company.readers || []).some((reader) => matches(reader.email, needle))), [data, needle]);
  const recent = useMemo(() => (data?.recent || []).filter((event) => !needle || matches(event.email, needle)
    || matches(event.ticker, needle)), [data, needle]);
  const topOpens = Math.max(0, ...(data?.users || []).map((user) => user.opens));
  const topCompanyOpens = Math.max(0, ...(data?.companies || []).map((company) => company.opens));
  const totals = data?.totals;

  return <section className="panel admin-table-panel activity-panel">
    <div className="panel-heading">
      <div><p className="eyebrow">Access management</p><h2>Report activity</h2></div>
      {viewTabs}
    </div>
    <div className="activity-toolbar">
      <label>Period
        <select aria-label="Activity period" value={days} onChange={(event) => setDays(Number(event.target.value))}>
          {RANGES.map((range) => <option key={range} value={range}>Last {range} days</option>)}
        </select>
      </label>
      <fieldset className="activity-audience">
        <legend>Include views</legend>
        {audienceOptions.map(({ id, label }) => <label className="activity-check" key={id}>
          <input
            type="checkbox"
            checked={audiences.includes(id)}
            onChange={(event) => setAudiences((current) => {
              const selected = new Set(current);
              if (event.target.checked) selected.add(id);
              else selected.delete(id);
              return audienceOptions.filter((audience) => selected.has(audience.id)).map((audience) => audience.id);
            })}
          />
          {label}
        </label>)}
      </fieldset>
      <button className="text-action" type="button" onClick={load} disabled={state.loading}>{state.loading ? 'Loading…' : 'Refresh'}</button>
      {data?.range && <span className="quality-label">{formatDay(data.range.start)} – {formatDay(data.range.end)} · {data.range.tz}</span>}
    </div>

    {state.error && <div className="error-box admin-message" role="alert">{state.error}</div>}
    {!data && state.loading && <div className="empty-state">Loading report activity...</div>}

    {totals && <>
      <div className="activity-metrics" aria-label="Report activity totals">
        <div><span>Reports opened</span><strong>{totals.opens}</strong></div>
        <div><span>First Looks</span><strong>{totals.firstlook}</strong></div>
        <div><span>Deep Dives</span><strong>{totals.deepdive}</strong></div>
        <div><span>Active readers</span><strong>{totals.readers}</strong></div>
        <div><span>Companies read</span><strong>{totals.companies}</strong></div>
        <div><span>Reading time</span><strong>{formatDuration(totals.seconds)}</strong></div>
      </div>

      {totals.sections === 0 ? <div className="empty-state">No report views recorded in this period yet. Views are recorded when a signed-in reader keeps a report section on screen.</div> : <>
        <div className="activity-grid">
          <div className="activity-card">
            <h3>Reports opened per day</h3>
            <DailyChart days={data.by_day || []} />
          </div>
          <div className="activity-card">
            <h3>When readers open reports</h3>
            <HourHeatmap grid={data.by_hour || []} zone={data.range?.tz || zone} />
          </div>
        </div>

        <div className="activity-card">
          <h3>Heavy users <span className="quality-label">{users.length} {users.length === 1 ? 'reader' : 'readers'}</span></h3>
          {users.length ? <div className="admin-table-scroll">
            <table className="admin-table activity-table">
              <thead><tr><th>#</th><th>Reader</th><th className="r">Opens</th><th>First Look / Deep Dive</th><th className="r">Companies</th><th className="r">Active days</th><th className="r">Sections</th><th className="r">Reading time</th><th>Last seen</th></tr></thead>
              <tbody>{users.map((user) => {
                const key = user.user_id || user.email;
                const open = expandedUser === key;
                return <React.Fragment key={key}>
                  <tr>
                    <td>{(data.users || []).indexOf(user) + 1}</td>
                    <td><button className="account-expand" aria-expanded={open} onClick={() => setExpandedUser(open ? '' : key)}><strong>{user.email || user.user_id}</strong><small>{planLabel(user.plan)}</small></button></td>
                    <td className="r"><span className="activity-count">{user.opens}<Share value={user.opens} max={topOpens} /></span></td>
                    <td><Split item={user} /></td>
                    <td className="r">{user.companies_count}</td>
                    <td className="r">{user.active_days}</td>
                    <td className="r">{user.sections}</td>
                    <td className="r">{formatDuration(user.seconds)}</td>
                    <td>{formatWhen(user.last_seen)}</td>
                  </tr>
                  {open && <tr className="admin-detail-row"><td colSpan="9">
                    <table className="admin-table activity-subtable" aria-label={`Companies read by ${user.email}`}>
                      <thead><tr><th>Company</th><th className="r">Opens</th><th>First Look / Deep Dive</th><th className="r">Sections</th><th className="r">Reading time</th><th>Last viewed</th></tr></thead>
                      <tbody>{(user.companies || []).map((company) => <tr key={company.ticker}>
                        <td><strong>{company.ticker}</strong></td><td className="r">{company.opens}</td><td><Split item={company} /></td>
                        <td className="r">{company.sections}</td><td className="r">{formatDuration(company.seconds)}</td><td>{formatWhen(company.last_viewed)}</td>
                      </tr>)}</tbody>
                    </table>
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table>
          </div> : <div className="empty-state compact-empty">No readers match this search.</div>}
        </div>

        <div className="activity-card">
          <h3>Companies being read <span className="quality-label">{companies.length} {companies.length === 1 ? 'company' : 'companies'}</span></h3>
          {companies.length ? <div className="admin-table-scroll">
            <table className="admin-table activity-table">
              <thead><tr><th>Company</th><th className="r">Opens</th><th>First Look / Deep Dive</th><th className="r">Readers</th><th className="r">Sections</th><th className="r">Reading time</th><th>Last viewed</th></tr></thead>
              <tbody>{companies.map((company) => {
                const open = expandedCompany === company.ticker;
                return <React.Fragment key={company.ticker}>
                  <tr>
                    <td><button className="account-expand" aria-expanded={open} onClick={() => setExpandedCompany(open ? '' : company.ticker)}><strong>{company.ticker}</strong><small>{company.readers_count} {company.readers_count === 1 ? 'reader' : 'readers'}</small></button></td>
                    <td className="r"><span className="activity-count">{company.opens}<Share value={company.opens} max={topCompanyOpens} /></span></td>
                    <td><Split item={company} /></td>
                    <td className="r">{company.readers_count}</td>
                    <td className="r">{company.sections}</td>
                    <td className="r">{formatDuration(company.seconds)}</td>
                    <td>{formatWhen(company.last_viewed)}</td>
                  </tr>
                  {open && <tr className="admin-detail-row"><td colSpan="7">
                    <table className="admin-table activity-subtable" aria-label={`Readers of ${company.ticker}`}>
                      <thead><tr><th>Reader</th><th className="r">Opens</th><th>First Look / Deep Dive</th><th className="r">Sections</th><th className="r">Reading time</th><th>Last viewed</th></tr></thead>
                      <tbody>{(company.readers || []).map((reader) => <tr key={reader.user_id || reader.email}>
                        <td>{reader.email || reader.user_id}</td><td className="r">{reader.opens}</td><td><Split item={reader} /></td>
                        <td className="r">{reader.sections}</td><td className="r">{formatDuration(reader.seconds)}</td><td>{formatWhen(reader.last_viewed)}</td>
                      </tr>)}</tbody>
                    </table>
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table>
          </div> : <div className="empty-state compact-empty">No companies match this search.</div>}
        </div>

        <div className="activity-grid">
          <div className="activity-card">
            <h3>Most-read sections</h3>
            <div className="admin-table-scroll">
              <table className="admin-table activity-table">
                <thead><tr><th>Section</th><th>Report</th><th className="r">Views</th><th className="r">Readers</th><th className="r">Avg time</th></tr></thead>
                <tbody>{(data.sections || []).map((section) => <tr key={`${section.product}:${section.tab_id}`}>
                  <td>{section.tab_label || section.tab_id || '—'}</td><td>{productLabel(section.product)}</td>
                  <td className="r">{section.views}</td><td className="r">{section.readers}</td>
                  <td className="r">{formatDuration(section.views ? section.seconds / section.views : 0)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </div>
          <div className="activity-card">
            <h3>Recent opens</h3>
            {recent.length ? <ol className="activity-feed">{recent.map((event, index) => <li key={`${event.viewed_at}-${index}`}>
              <time dateTime={event.viewed_at}>{formatWhen(event.viewed_at)}</time>
              <span><strong>{event.email || 'Unknown reader'}</strong> {(ENTRIES[event.entry] || 'Opened').toLowerCase()} <b>{event.ticker}</b> {productLabel(event.product)}{event.seconds ? ` · ${formatDuration(event.seconds)}` : ''}</span>
            </li>)}</ol> : <div className="empty-state compact-empty">No opens match this search.</div>}
          </div>
        </div>
      </>}
    </>}
  </section>;
};

export default UserActivityPanel;
